import { useEffect, useState } from 'react';
import WidgetScreen from './screens/WidgetScreen';
import { useGps } from './lib/gps';
import { useSettings } from './lib/settings';
import { EMPTY_COURSE, loadCourse } from './lib/courses';
import { BRAND } from './lib/brand';
import { distanceM } from './lib/geo';
import type { Course } from './lib/types';

/**
 * Standalone host for /widget.
 *
 * Runs the minimum needed to render one glanceable tile: settings, a GPS fix,
 * and the active course. No fleet socket, no weather polling, no map — a widget
 * that drains the head unit's battery or keeps a WebSocket open all day is a
 * widget that gets uninstalled.
 */
export default function WidgetHost() {
  const { settings } = useSettings();
  const { fix } = useGps();
  const [course, setCourse] = useState<Course>(EMPTY_COURSE);

  useEffect(() => {
    const id = settings.courseId;
    if (id == null) return setCourse(EMPTY_COURSE);
    loadCourse(id)
      .then(({ course: c }) => setCourse(c))
      .catch(() => {});
  }, [settings.courseId]);

  // Pick the hole the buggy is actually on, same rule as the main app.
  const hole = course.holes.length
    ? (fix
        ? course.holes.reduce((best, h) =>
            Math.min(
              distanceM(fix.pos, h.tees[settings.teeColour] ?? h.tees.White),
              distanceM(fix.pos, h.green.middle)
            ) <
            Math.min(
              distanceM(fix.pos, best.tees[settings.teeColour] ?? best.tees.White),
              distanceM(fix.pos, best.green.middle)
            )
              ? h
              : best
          )
        : course.holes[0])
    : null;

  const open = () => {
    // Launcher tiles run in their own WebView; hand off to the full app.
    location.href = '/';
  };

  if (!hole) {
    return (
      <button className="widget" onClick={open}>
        <div className="widget-head">
          <span className="widget-brand">{BRAND.name}</span>
        </div>
        <div className="widget-label">{course.id ? 'COURSE NOT SURVEYED' : 'NO COURSE YET'}</div>
        <div className="widget-course">{course.name || 'Pick where you are playing'}</div>
        <div className="widget-sub">
          {course.id
            ? 'Tap to open · run Survey mode to capture the holes'
            : 'Tap to open and search 1,449 courses'}
        </div>
      </button>
    );
  }

  return (
    <WidgetScreen
      course={course}
      hole={hole}
      teeColour={settings.teeColour}
      unit={settings.unit}
      fix={fix}
      onOpen={open}
      brand={BRAND.name}
    />
  );
}
